import * as PIXI from 'pixi.js';
import { assertNumberBetween } from '../../validators';
import type { UpdateFlowMixinSettings } from '../updateFlowMixin';
import { TypedUpdateFlowMixin } from '../updateFlowMixin';
import type { SerializableMixinSettings } from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import type { BaseAsset } from '../asset/BaseAsset';
import type { Resolution } from '../../types';

/**
 * The edge of a mask being dragged when it is resized.
 */
export type MaskEdge = 'left' | 'right' | 'top' | 'bottom';

/**
 * A mask's saved data after relocating it to another reference resolution.
 * @internal
 */
export type RelocatedMaskData = {
    data: object;
    deltaX: number;
    deltaY: number;
};

export type StencilMaskSettings = {
    updateTarget: BaseAsset;
    getReferenceResolution: () => Resolution;
    parent?: never; // parent should not be used
} & SerializableMixinSettings &
    UpdateFlowMixinSettings;

/**
 * The StencilMask is a base class for our masks that can be applied to a BaseAsset.
 *
 * Stencil masking discards pixels that don't overlap with the pixels rendered into the stencil buffer.
 * It is not as fast as a Scissor Mask, but nonetheless still quite performant.
 *
 * Note that this class does not use the Parent mixin, because setting the parent property on a PIXI.Graphics
 * makes it get positioned to relative to the parent. However, our masks were originally built to be
 * globally positioned, so it's simpler to just not use the parent property. This does mean though that
 * this class doesn't exactly follow our pattern of using the mixins, but it's pretty close. The notable
 * difference is that it implements its own updateTarget to notify a change instead of using its parent.
 */
export abstract class StencilMask extends TypedUpdateFlowMixin(
    TypedSerializableMixin(PIXI.Graphics),
) {
    #transition: number = 0;
    protected updateTarget: BaseAsset;
    /** @internal */
    _getReferenceResolution: () => Resolution;

    constructor(settings: StencilMaskSettings) {
        super(settings);
        this.updateTarget = settings.updateTarget;
        this._getReferenceResolution = settings.getReferenceResolution;
    }

    abstract getMaxBounds(): PIXI.Rectangle;

    /**
     * Translates the shape by a delta in reference-resolution pixels. Subclasses convert it to
     * their own units (normalized for PolygonMask, reference pixels for CircleMask). The shape is
     * not clamped to the reference resolution. Mutates the persisted start/end geometry, never
     * in-flight morph state.
     */
    abstract moveBy(deltaX: number, deltaY: number): void;

    /**
     * Resizes the shape by dragging one edge. The delta is in reference-resolution pixels, signed
     * along the edge's axis: negative moves a left/right edge left and a top/bottom edge up.
     * Shapes with independent axes move only the dragged edge and anchor the opposite one; a
     * circle scales about its centre instead. Mutates the persisted start/end geometry, never
     * in-flight morph state.
     */
    abstract resizeBy(edge: MaskEdge, delta: number): void;

    /**
     * The mask's saved data carried from one reference resolution to another: its centre keeps
     * its relative place in the frame and its size in pixels stays the same. Also returns how far
     * the centre moved, in pixels, so whatever the mask clips can move with it.
     * @internal
     */
    abstract _saveRelocated(from: Resolution, to: Resolution): RelocatedMaskData;

    get root() {
        return this.updateTarget.root;
    }

    override _emitChanged() {
        this.updateTarget.requiresRedraw = true;
    }

    /**
     * The point along the shape's progression from its start state to end state.
     */
    get transition() {
        return this.#transition;
    }

    set transition(value: number) {
        if (this.transition === value) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.#transition = value;
    }

    override get propertiesToSerialize(): string[] {
        return [];
    }

    override get readonlyProperties() {
        return ['classType'];
    }

    createHelpers(): PIXI.Container[] {
        return [];
    }

    updateForRender() {}

    /** @internal */
    _hasArea(): boolean {
        const { width, height } = this.bounds;
        return width > 0 && height > 0;
    }
}
